import React, { FC, useState, useCallback } from "react";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { Fade } from "react-awesome-reveal";
import { useI18next } from "gatsby-plugin-react-i18next";
// import scrollToElement from "scroll-to-element";
import { SbGatsbyStory, useStoryblokState } from "gatsby-source-storyblok";
import { useGroupedFaqLibrary } from "@app/components/faq-page/hooks/use-grouped-faq-library/useGroupedFaqLibrary";
import { useViewVariant } from "@app/hooks/use-view-variant/useViewVariant";
import { DEFAULT_FADE_DURATION } from "@app/constants";
// import { usePageTranslation } from "@app/hooks";
import { FAQGroup } from "../common/faq-group/FAQGroup";
import { Asset } from "../base-page/models";
// import { Search } from "./components/search/Search";

type PageContent = {
  content: {
    heroTitle: string;
    background: Asset;
    faqLibrary?: string[];
  };
};

type Props = {
  content: SbGatsbyStory;
};

export const FAQPage: FC<Props> = ({ content }) => {
  const entry = useStoryblokState(content) as unknown as PageContent;
  const { heroTitle, background, faqLibrary } = entry.content;
  const { isContentOnly } = useViewVariant();
  // const { t } = usePageTranslation();
  const [selectedFAQ, setSelectedFAQ] = useState("");
  const { language, defaultLanguage } = useI18next();
  const groupedQuestionsPool = useGroupedFaqLibrary(
    language === defaultLanguage ? "default" : language,
    isContentOnly,
    true,
    faqLibrary,
  );

  const onSetActiveQuestion = useCallback(
    (questionId: string) => {
      setSelectedFAQ((prev) => (prev === questionId ? "" : questionId));
    },
    [setSelectedFAQ],
  );

  return (
    <>
      <Fade
        duration={DEFAULT_FADE_DURATION}
        triggerOnce
        className="relative z-[1]"
      >
        <div
          className={classNames(
            "relative flex flex-col gap-y-10 px-5 pb-20 pt-40",
            "lg:pt-46 lg:px-16 lg:pb-25",
            "xl:w-full xl:pb-40",
            "xl:gap-y-0 xl:px-24",
            "xl:items-center xl:justify-center",
          )}
        >
          <div
            className={classNames(
              "overflow-hidden",
              "absolute bottom-0 left-0 right-0 top-0 block",
            )}
          >
            <GatsbyImage
              image={getGatsbyImage(background.filename, {
                layout: "fullWidth",
              })}
              alt={background.alt}
              className={classNames(
                "rounded-sm blur-lg",
                "h-full w-full xl:rounded-none",
              )}
            />
          </div>
          <div
            className={classNames(
              "relative flex w-full flex-col justify-center",
              "text-primary-content",
              "lg:max-w-3/4",
              "xl:h-full xl:max-w-container xl:text-center",
              "xl:order-last xl:items-center xl:gap-15",
            )}
          >
            <div className={classNames("xl:w-1/2")}>
              <h1 className="mb-5 text-large text-primary-content lg:text-xx-large xl:mb-10 xl:text-huge">
                {heroTitle}
              </h1>
              {/* <Search
                items={faqList}
                getValue={(item) => item.question}
                onChange={(item) => {
                  setSelectedFAQ(item.id);
                  setTimeout(
                    () =>
                      scrollToElement(`#${item.id}`, {
                        offset: -80,
                        ease: "linear",
                        duration: 500,
                      }),
                    100,
                  );
                }}
                placeholder={t("search")}
              /> */}
            </div>
          </div>
        </div>
      </Fade>

      <div
        className={classNames(
          "flex justify-center px-5 py-20 text-base-400",
          "lg:px-16 lg:py-25",
          "xl:px-24 xl:py-40",
        )}
      >
        <div className="flex max-w-container justify-center">
          <div className="w-full space-y-15 lg:w-10/12">
            {groupedQuestionsPool?.map((group) => (
              <FAQGroup
                key={group.id}
                data={group}
                onSetActive={(id) => onSetActiveQuestion(id)}
                activeQuestion={selectedFAQ}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
